import { useState, useEffect, useMemo, useContext } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Soft UI Dashboard PRO React components
import Sidenav from "examples/Sidenav";

// Protected Route Component
import ProtectedRoute from "components/ProtectedRoute";

// Soft UI Dashboard PRO React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Soft UI Dashboard PRO React routes
import routes from "routes";

// Soft UI Dashboard PRO React contexts
import { useSoftUIController, setMiniSidenav, AuthProvider, AuthContext } from "context";

import { toPublicAssetUrl } from "services/assets";

// Images
import brand from "assets/images/logo-ct.png";

const RESTRICTED_DOCUMENTATION_SLUG = "sboffice";
const RESTRICTED_DOCUMENTATION_ROUTE = `/documentation/${RESTRICTED_DOCUMENTATION_SLUG}`;

const findRouteByKey = (allRoutes, key) => {
  for (const route of allRoutes) {
    if (route.key === key) {
      return route;
    }
    if (route.collapse) {
      const found = findRouteByKey(route.collapse, key);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

const buildRestrictedPortalRoutes = () => {
  const login = findRouteByKey(routes, "login");
  const dashboard = findRouteByKey(routes, "dashboard");
  const iaas = findRouteByKey(routes, "iaas");
  const iaasServer = findRouteByKey(routes, "iaas-server");
  const iaasDetail = findRouteByKey(routes, "iaas-detail");
  const sboffice = findRouteByKey(routes, "services/sboffice");
  const sbofficeDetail = findRouteByKey(routes, "sboffice-detail");
  const support = findRouteByKey(routes, "Support");
  const documentation = findRouteByKey(routes, "documentation");
  const documentationProductPage = findRouteByKey(routes, "documentation-product-page");

  return [
    login,
    dashboard ? { ...dashboard, name: "Home" } : null,
    { type: "title", title: "Services", key: "title-services-restricted" },
    iaasServer
      ? {
          ...iaasServer,
          type: "collapse",
          name: "IaaS",
          key: "iaas",
          icon: iaas?.icon,
          route: "/iaas/server",
          noCollapse: true,
        }
      : null,
    sboffice,
    { type: "title", title: "Hilfe", key: "title-help-restricted" },
    support,
    documentationProductPage
      ? {
          ...documentationProductPage,
          type: "collapse",
          name: "Dokumentation",
          key: "documentation",
          icon: documentation?.icon,
          route: documentationProductPage.route,
          navRoute: RESTRICTED_DOCUMENTATION_ROUTE,
          noCollapse: true,
        }
      : null,
    iaasDetail,
    sbofficeDetail,
  ].filter(Boolean);
};

// Innere App-Komponente mit Zugriff auf AuthContext
function AppContent() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const {
    isAuthenticated,
    isLoading,
    partnerSettings,
    activePortalSettings,
    isRestrictedPortal,
    portalSettingsLoading,
    user,
  } = useContext(AuthContext);

  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });
    setRtlCache(cacheRtl);
  }, []);

  // Dynamisches Theme basierend auf Partner-Branding
  const activeTheme = useMemo(() => {
    if (!activePortalSettings?.primary_color) return theme;
    return createTheme({
      ...theme,
      palette: {
        ...theme.palette,
        primary: { main: activePortalSettings.primary_color, focus: activePortalSettings.primary_color },
        info: { main: activePortalSettings.primary_color, focus: activePortalSettings.primary_color },
      },
    });
  }, [activePortalSettings]);

  const activeThemeRTL = useMemo(() => {
    if (!activePortalSettings?.primary_color) return themeRTL;
    return createTheme({
      ...themeRTL,
      palette: {
        ...themeRTL.palette,
        primary: { main: activePortalSettings.primary_color, focus: activePortalSettings.primary_color },
        info: { main: activePortalSettings.primary_color, focus: activePortalSettings.primary_color },
      },
    });
  }, [activePortalSettings]);

  // Favicon und Seitentitel dynamisch setzen
  useEffect(() => {
    let faviconEl = document.querySelector("link[rel~='icon']");
    if (!faviconEl) {
      faviconEl = document.createElement("link");
      faviconEl.rel = "icon";
      document.head.appendChild(faviconEl);
    }
    if (faviconEl) {
      faviconEl.href = toPublicAssetUrl(activePortalSettings?.favicon_url, { fallback: "/favicon.png" });
    }
    document.title = activePortalSettings?.brand_name || "UDS Kundenportal";
  }, [activePortalSettings]);

  const restrictedPortalRoutes = useMemo(() => buildRestrictedPortalRoutes(), []);

  // Sidenav-Routen: UDS-Produkte nach enabled_products filtern
  const filteredRoutes = useMemo(() => {
    if (isRestrictedPortal) {
      return restrictedPortalRoutes;
    }

    const allowed = user?.isPartner
      ? partnerSettings?.effective_enabled_products
      : partnerSettings?.enabled_products;
    if (!allowed) return routes;
    const UDS_PRODUCT_KEYS = ['ecm', 'epm', 'auto', 'sboffice'];
    return routes.filter(r => {
      if (r.productSlug && UDS_PRODUCT_KEYS.includes(r.productSlug)) {
        return allowed.includes(r.productSlug);
      }
      return true;
    });
  }, [isRestrictedPortal, partnerSettings, restrictedPortalRoutes, user]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }
      return null;
    });

  const protectedNavigate = (to) => (
    <ProtectedRoute>
      <Navigate to={to} replace />
    </ProtectedRoute>
  );

  const restrictedRedirectRoutes = isRestrictedPortal ? (
    <>
      <Route path="/iaas" element={protectedNavigate("/iaas/server")} />
      <Route path="/iaas/discover" element={protectedNavigate("/iaas/server")} />
      <Route path="/saas/*" element={protectedNavigate("/dashboard")} />
      <Route path="/services/ecm/*" element={protectedNavigate("/dashboard")} />
      <Route path="/services/epm/*" element={protectedNavigate("/dashboard")} />
      <Route path="/services/auto/*" element={protectedNavigate("/dashboard")} />
      <Route path="/billing" element={protectedNavigate("/dashboard")} />
      <Route path="/partner-center" element={protectedNavigate("/dashboard")} />
      <Route path="/order/*" element={protectedNavigate("/dashboard")} />
      <Route path="/documentation" element={protectedNavigate(RESTRICTED_DOCUMENTATION_ROUTE)} />
      <Route path="/documentation/iaas/*" element={protectedNavigate(RESTRICTED_DOCUMENTATION_ROUTE)} />
      <Route path="/documentation/saas/*" element={protectedNavigate(RESTRICTED_DOCUMENTATION_ROUTE)} />
      <Route path="/documentation/ecm/*" element={protectedNavigate(RESTRICTED_DOCUMENTATION_ROUTE)} />
      <Route path="/documentation/epm/*" element={protectedNavigate(RESTRICTED_DOCUMENTATION_ROUTE)} />
      <Route path="/documentation/auto/*" element={protectedNavigate(RESTRICTED_DOCUMENTATION_ROUTE)} />
    </>
  ) : null;

  const renderAppRoutes = () => (
    <Routes>
      <Route path="/" element={
        <ProtectedRoute>
          <Navigate to="/dashboard" replace />
        </ProtectedRoute>
      } />
      {getRoutes(filteredRoutes)}
      {restrictedRedirectRoutes}
      <Route path="*" element={
        <ProtectedRoute>
          <Navigate to="/dashboard" />
        </ProtectedRoute>
      } />
    </Routes>
  );

  // Sidenav nur anzeigen wenn authentifiziert und Auth-Check abgeschlossen
  const showSidenav = layout === "dashboard" && isAuthenticated && !isLoading && !portalSettingsLoading;

  const activeBrand = toPublicAssetUrl(activePortalSettings?.logo_url, { fallback: brand });
  const activeBrandName = activePortalSettings?.brand_name || "UDS-Portal";

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={activeThemeRTL}>
        <CssBaseline />
        {showSidenav && (
          <Sidenav
            color={sidenavColor}
            brand={activeBrand}
            brandName={activeBrandName}
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        )}
        {renderAppRoutes()}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      {showSidenav && (
        <Sidenav
          color={sidenavColor}
          brand={activeBrand}
          brandName={activeBrandName}
          routes={filteredRoutes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
        />
      )}
      {renderAppRoutes()}
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
